import { useNavigate } from "@tanstack/react-router";
import { Search, User } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "@/app/components/Avatar";
import { MemberBadges } from "@/app/components/MemberBadges";
import { useAccessLogger } from "@/app/hooks/useAccessLogger";
import { useDagStore } from "@/app/hooks/useDagStore";

interface MemberSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MemberSearchModal({ isOpen, onClose }: MemberSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const { trackComponentOpen, trackButtonClick } = useAccessLogger();
  const inputRef = useRef<HTMLInputElement>(null);

  const teamMembers = useDagStore((state) => state.teamMembersFiltered);

  // Filter members based on search query
  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) {
      return teamMembers;
    }

    const query = searchQuery.toLowerCase();
    return teamMembers.filter((member) => {
      return (
        member.name.toLowerCase().includes(query) ||
        member.role?.toLowerCase().includes(query) ||
        member.team?.toLowerCase().includes(query) ||
        member.notionUsers.some((user) => user.email?.toLowerCase().includes(query))
      );
    });
  }, [searchQuery, teamMembers]);

  // Reset selected index when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, []);

  // Track when modal opens and autofocus input
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (isOpen) {
      trackComponentOpen("member-search-modal");
      // Focus input after modal animation
      timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isOpen, trackComponentOpen]);

  const handleSelectMember = useCallback(
    (member: (typeof teamMembers)[0]) => {
      trackButtonClick("member-search:select-member", {
        memberId: member.id,
        searchQuery,
      });
      navigate({ to: "/member/$memberId", params: { memberId: member.id } });
      onClose();
      setSearchQuery("");
    },
    [navigate, onClose, searchQuery, trackButtonClick],
  );

  // Handle keyboard navigation and selection
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredMembers.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" && filteredMembers[selectedIndex]) {
        e.preventDefault();
        handleSelectMember(filteredMembers[selectedIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, filteredMembers, selectedIndex, onClose, handleSelectMember]);

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSelectedIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] pointer-events-none">
        <div
          className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[60vh] flex flex-col pointer-events-auto animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-200">
            <Search className="w-5 h-5 text-stone-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search team members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 outline-none text-lg placeholder:text-stone-400"
            />
            <div className="flex items-center gap-1 text-xs text-stone-400">
              <kbd className="px-2 py-1 bg-stone-100 rounded text-stone-600">ESC</kbd>
            </div>
          </div>

          {/* Results */}
          <div className="overflow-y-auto flex-1">
            {filteredMembers.length === 0 ? (
              <div className="px-4 py-8 text-center text-stone-500">
                <User className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No team members found</p>
              </div>
            ) : (
              <div className="py-2">
                {filteredMembers.map((member, index) => (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => handleSelectMember(member)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left ${
                      index === selectedIndex ? "bg-blue-50" : "hover:bg-stone-50"
                    }`}
                  >
                    <Avatar member={member} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-stone-900 truncate">{member.name}</div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <MemberBadges role={member.role} team={member.team} />
                      </div>
                    </div>
                    {index === selectedIndex && (
                      <kbd className="px-2 py-1 bg-stone-100 rounded text-xs text-stone-600">↵</kbd>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-stone-200 bg-stone-50 text-xs text-stone-500 flex items-center gap-4">
            <div className="flex items-center gap-1">
              <kbd className="px-2 py-1 bg-white rounded text-stone-600">↑</kbd>
              <kbd className="px-2 py-1 bg-white rounded text-stone-600">↓</kbd>
              <span>navigate</span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="px-2 py-1 bg-white rounded text-stone-600">↵</kbd>
              <span>select</span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="px-2 py-1 bg-white rounded text-stone-600">ESC</kbd>
              <span>close</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
